import React, { useState, useEffect } from "react";
import { Box, Typography, Paper } from "@mui/material";

// Malayalam words and their clues
const questions = [
  { q: "കേരളത്തിലെ ഏറ്റവും നീളമുള്ള നദി?", a: "പെരിയാർ" },
  { q: "സബരിമലക്ക് അടുത്ത് പോകുന്ന നദി?", a: "പമ്പ" },
  { q: "മലപ്പുറം ജില്ലയിലൂടെയുള്ള നദി", a: "കുന്തി" },
  { q: "ഭാരതപ്പുഴക്ക് മറ്റൊരു പേര്", a: "നിള" },
  { q: "കോട്ടയം വഴി ഒഴുകുന്ന നദി", a: "മീനച്ചിൽ" },
];

// Utility: Split Malayalam word into grapheme clusters
function splitMalayalamWord(word) {
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter("ml", { granularity: "grapheme" });
    return [...segmenter.segment(word)].map((s) => s.segment);
  }
  return Array.from(word);
}

// Grid constants
const GRID_SIZE = 20;
const emptyGrid = () =>
  Array(GRID_SIZE)
    .fill(null)
    .map(() => Array(GRID_SIZE).fill(""));

// Check if word fits at location with given direction
const canPlaceWord = (grid, word, row, col, dir) => {
  for (let i = 0; i < word.length; i++) {
    const r = dir === "across" ? row : row + i;
    const c = dir === "across" ? col + i : col;
    if (
      r >= GRID_SIZE ||
      c >= GRID_SIZE ||
      (grid[r][c] !== "" && grid[r][c] !== word[i])
    ) {
      return false;
    }
  }
  return true;
};

// Place word on grid
const placeWord = (grid, word, row, col, dir) => {
  for (let i = 0; i < word.length; i++) {
    const r = dir === "across" ? row : row + i;
    const c = dir === "across" ? col + i : col;
    grid[r][c] = word[i];
  }
};

// Remove word from grid
const removeWord = (grid, word, row, col, dir) => {
  for (let i = 0; i < word.length; i++) {
    const r = dir === "across" ? row : row + i;
    const c = dir === "across" ? col + i : col;
    grid[r][c] = "";
  }
};

// Recursive backtracking function
const solveKrissKross = (grid, words, index = 0) => {
  if (index >= words.length) return true;

  const word = splitMalayalamWord(words[index].a);
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      for (let dir of ["across", "down"]) {
        if (canPlaceWord(grid, word, row, col, dir)) {
          placeWord(grid, word, row, col, dir);
          if (solveKrissKross(grid, words, index + 1)) return true;
          removeWord(grid, word, row, col, dir);
        }
      }
    }
  }

  return false;
};

// Trim grid to visible area
const getGridBoundaries = (grid) => {
  let minRow = GRID_SIZE,
    maxRow = 0,
    minCol = GRID_SIZE,
    maxCol = 0;

  for (let i = 0; i < GRID_SIZE; i++) {
    for (let j = 0; j < GRID_SIZE; j++) {
      if (grid[i][j]) {
        minRow = Math.min(minRow, i);
        maxRow = Math.max(maxRow, i);
        minCol = Math.min(minCol, j);
        maxCol = Math.max(maxCol, j);
      }
    }
  }

  return { minRow, maxRow, minCol, maxCol };
};

const KrissKrossGame = () => {
  const [grid, setGrid] = useState(emptyGrid());

  useEffect(() => {
    const newGrid = emptyGrid();
    const success = solveKrissKross(newGrid, questions);
    if (success) setGrid(newGrid);
  }, []);

  const { minRow, maxRow, minCol, maxCol } = getGridBoundaries(grid);

  return (
    <Box p={4}>
      <Typography variant="h5" gutterBottom>
        Malayalam Kriss Kross Game
      </Typography>

      <Box
        sx={{
          display: "inline-block",
          mb: 3,
        }}
      >
        {grid.slice(minRow, maxRow + 1).map((row, rowIndex) => (
          <Box key={rowIndex} display="flex">
            {row.slice(minCol, maxCol + 1).map((cell, colIndex) =>
              cell ? (
                <Paper
                  key={`${rowIndex}-${colIndex}`}
                  elevation={1}
                  sx={{
                    width: 42,
                    height: 42,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 20,
                    fontWeight: "bold",
                    border: "1px solid #aaa",
                    backgroundColor: "#fff",
                  }}
                >
                  {cell}
                </Paper>
              ) : (
                <Box key={`${rowIndex}-${colIndex}`} sx={{ width: 42, height: 42 }} />
              )
            )}
          </Box>
        ))}
      </Box>

      <Box mt={4}>
        {questions.map((item, index) => (
          <Typography key={index} mb={1}>
            <strong>{index + 1}.</strong> {item.q}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default KrissKrossGame;
